import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Landing: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
  });
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      modalRef.current?.querySelector('input')?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { name?: string; email?: string } = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const response = await fetch('/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          plan: 'CimmpleFlow - Landing Page',
          service: 'CimmpleFlow',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to send request. Please try again.';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          errorMessage = `Server error (${response.status}): ${errorText.substring(0, 100)}`;
        }
        alert(errorMessage);
        return;
      }

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
      } else {
        alert(result.message || 'Failed to send request. Please try again.');
      }
    } catch (error) {
      console.error('Email submission error:', error);
      alert('Failed to send request. Please check your connection and try again, or contact us directly at contact@cimmple.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsSubmitted(false);
    setFormData({ name: '', email: '', company: '' });
    setErrors({});
  };

  const keyFeatures = [
    {
      title: 'Quotations',
      description: 'Create, manage, and track quotations with VendorConnect™ for seamless supplier collaboration.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      title: 'Job Management',
      description: 'Track jobs in real-time with live updates, scheduling, and resource allocation.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    },
    {
      title: 'Quality Control',
      description: 'Ensure product quality with live reporting, inspections, and compliance tracking.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Real-Time Analytics',
      description: 'Generate scheduled reports and gain insights with comprehensive business analytics.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ];

  const benefits = [
    'Cloud-based access from anywhere, anytime',
    'Real-time live tracking & updates',
    'Streamlined business workflows',
    'Complete operational visibility',
    'Reduced manual data entry',
    'Improved decision making with data-driven insights',
  ];

  return (
    <div className="pt-0">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-dark-800/50 border border-primary-200 dark:border-dark-700 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-slate-700 dark:text-dark-300 font-medium">Transform Your Manufacturing Operations</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display font-bold mb-6 animate-slide-up">
            <span className="text-slate-900 dark:text-white">Manufacturing & Logistics</span>
            <br />
            <span className="gradient-text">Made Simple</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-slate-700 dark:text-dark-300 max-w-3xl mx-auto mb-10 animate-slide-up animate-delay-100">
            CimmpleFlow is your complete cloud-based ERP solution. From quotation management to job tracking, 
            quality control to accounting—everything you need to streamline operations and drive growth.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up animate-delay-200 mb-12">
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary text-lg px-8 py-4"
            >
              Start Free Trial
              <svg className="inline-block ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            <Link to="/pricing?product=cimmple" className="btn-secondary text-lg px-8 py-4">
              View Pricing
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-slate-600 dark:text-dark-400 animate-fade-in animate-delay-300">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>3-month free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>All features included</span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-slate-400 dark:border-dark-600 flex justify-center pt-2">
            <div className="w-1 h-2 rounded-full bg-slate-500 dark:bg-dark-400" />
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
              Powerful Features
            </span>
            <h2 className="section-title">
              Everything you need to <span className="gradient-text">succeed</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Comprehensive tools designed to streamline your manufacturing and logistics operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="card group hover:border-primary-500/30 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 group-hover:bg-primary-200 dark:group-hover:bg-primary-500/20 flex items-center justify-center mb-4 transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-dark-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
                Why Choose CimmpleFlow
              </span>
              <h2 className="section-title mb-6 text-left">
                Transform your <span className="gradient-text">business operations</span>
              </h2>
              <p className="text-slate-600 dark:text-dark-300 text-lg mb-8">
                Join manufacturing and logistics businesses that have streamlined their operations with CimmpleFlow.
              </p>
              
              <ul className="space-y-4">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-slate-700 dark:text-dark-300 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing Card */}
            <div className="card p-8 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-primary-500/10" />
              <div className="relative">
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4">
                  Simple, Transparent Pricing
                </h3>
                <div className="mb-6">
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-5xl font-display font-bold text-slate-900 dark:text-white">$49</span>
                    <span className="text-slate-500 dark:text-dark-400">/user/month</span>
                  </div>
                  <p className="text-slate-500 dark:text-dark-400 text-sm">
                    Billed annually. $55/user if billed monthly.
                  </p>
                </div>
                <ul className="space-y-3 mb-8 text-left">
                  {[
                    'Quotations (VendorConnect™)',
                    'Order & Job Management',
                    'Quality Control & Reporting',
                    'Advanced Accounting',
                    'Unlimited Jobs/Customers',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-dark-300">
                      <svg className="w-5 h-5 flex-shrink-0 text-primary-600 dark:text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowModal(true)}
                  className="btn-primary w-full"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
              Perfect For
            </span>
            <h2 className="section-title">
              Trusted by businesses across <span className="gradient-text">various industries</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              CimmpleFlow adapts to your industry needs, whether you're in manufacturing, logistics, or services.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              'Manufacturing companies',
              'Engineering firms',
              'Construction businesses',
              'Logistics & distribution',
              'Job shops & workshops',
              'Service-based businesses',
            ].map((useCase, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-white dark:bg-dark-800/30 border border-slate-200 dark:border-dark-700 hover:border-primary-500/50 transition-colors">
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-slate-700 dark:text-dark-300 text-sm font-medium">{useCase}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-6">
            Ready to transform your business?
          </h2>
          <p className="text-lg text-slate-600 dark:text-dark-300 mb-8 max-w-2xl mx-auto">
            Start your 3-month free trial today. No credit card required. Experience all CimmpleFlow features and see the difference it makes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary text-lg px-8 py-4"
            >
              Start Free Trial
              <svg className="inline-block ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
              Request Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Signup Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/50 dark:bg-dark-950/80 backdrop-blur-sm" onClick={closeModal} />
          <div ref={modalRef} className="relative card p-8 max-w-md w-full animate-scale-in">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-dark-800 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5 text-slate-500 dark:text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">You're all set!</h3>
                <p className="text-slate-600 dark:text-dark-300 mb-6">Welcome to CimmpleFlow! Check your email for next steps.</p>
                <button onClick={closeModal} className="btn-primary">Got it</button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">Start Your Free Trial</h3>
                <p className="text-slate-500 dark:text-dark-400 mb-6">
                  Start your 3-month free trial. No credit card required. All features included.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="modal-name" className="block text-sm font-medium text-slate-700 dark:text-dark-300 mb-2">Full Name *</label>
                    <input type="text" id="modal-name" name="name" value={formData.name} onChange={handleChange} className={`input-field ${errors.name ? 'border-red-500' : ''}`} placeholder="John Doe" />
                    {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="modal-email" className="block text-sm font-medium text-slate-700 dark:text-dark-300 mb-2">Work Email *</label>
                    <input type="email" id="modal-email" name="email" value={formData.email} onChange={handleChange} className={`input-field ${errors.email ? 'border-red-500' : ''}`} placeholder="john@company.com" />
                    {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="modal-company" className="block text-sm font-medium text-slate-700 dark:text-dark-300 mb-2">Company Name</label>
                    <input type="text" id="modal-company" name="company" value={formData.company} onChange={handleChange} className="input-field" placeholder="Acme Inc." />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating account...
                      </>
                    ) : (
                      'Start Free Trial'
                    )}
                  </button>
                </form>

                <p className="text-slate-400 dark:text-dark-500 text-xs text-center mt-4">By signing up, you agree to our Terms of Service and Privacy Policy.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;


